import React from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { Header, ThemeContext } from 'rds'
import { applyEffectToImage } from '../../utils'
import { POSITIONING } from '../../constants'

const StyledCarrouselHeroInner = styled.div(props => ({
    whiteSpace: 'nowrap',
    transition: `${props.transitionSpeed}s transform`,
    transform: `translateX(${props.currentPage ? `-${props.currentPage}00%` : '0'})`,
    height: `${props.height}px`,
    '& > *': {
        display: 'inline-grid',
        height: 'inherit',
        width: '100%',
    },
}))

const StyledSliderItem = styled.div(props => ({
    cursor: props.item.navigateToURL ? 'pointer' : null,
}))

const StyledSliderItemPicture = styled.div(props => ({
    height: 'inherit',
    width: '100%',
    backgroundImage: `url(${applyEffectToImage(
        props.item.picture?.url,
        props.item.picture?.effect,
        props.themeContext,
    )})`,
    backgroundSize: 'cover',
    backgroundPosition: `${props.item.picture?.verticalPosition} ${
        props.item.picture?.horizontalPosition
    }`,
    transition: '.2s background-position',
    '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: props.item.overlay?.color,
        opacity: props.item.overlay?.opacity,
    },
}))

const StyledSliderItemHeader = styled.div(props => {
    const { item, padding } = props
    const { header } = item
    return {
        padding,
        whiteSpace: 'break-spaces',
        width: 'fit-content',
        textAlign: header?.textAlignment,
        top:
            header?.verticalPosition === POSITIONING.TOP
                ? 0
                : header?.verticalPosition === POSITIONING.CENTER
                  ? '50%'
                  : null,
        bottom: header?.verticalPosition === POSITIONING.BOTTOM ? 0 : null,
        left:
            header?.horizontalPosition === POSITIONING.LEFT
                ? 0
                : header?.horizontalPosition === POSITIONING.CENTER
                  ? '50%'
                  : null,
        right: header?.horizontalPosition === POSITIONING.RIGHT ? 0 : null,
        transform: `translateY(${
            header?.verticalPosition === POSITIONING.CENTER ? '-50%' : 0
        }) translateX(${
            header?.horizontalPosition === POSITIONING.CENTER ? '-50%' : 0
        })`,
    }
})

const StyledCarrouselHeroSelector = styled.div(props => ({
    width: 10,
    height: 10,
    backgroundColor: 'white',
    borderRadius: '50%',
    margin: `0 var(--rds-spacingSmall)`,
    cursor: 'pointer',
    transition: `${props.transitionSpeed}s all`,
    opacity: props.isActive ? 1 : 0.5,
    transform: props.isActive ? 'scale(1)' : 'scale(0.8)',
}))

const Slider = ({
    items = [],
    height = 300,
    transitionSpeed = 0.2,
    millisecondsPerPage = 5000,
    padding = 20,
    margin,
}) => {
    const navigate = useNavigate()
    const themeContext = React.useContext(ThemeContext)
    const [currentPage, setCurrentPage] = React.useState(0)

    const resetInterval = React.useCallback(() => {
        if (interval.current) {
            clearInterval(interval.current)
        }
        interval.current = setInterval(() => {
            setCurrentPage(currentPage => {
                if (currentPage + 1 === items.length) {
                    return 0
                } else {
                    return currentPage + 1
                }
            })
        }, millisecondsPerPage)
    }, [millisecondsPerPage, items.length])

    const interval = React.useRef()
    React.useEffect(() => {
        setCurrentPage(0)
        resetInterval()
        return () => {
            clearInterval(interval.current)
        }
    }, [resetInterval])

    const handleItemClick = item => {
        if (item.navigateToURL) {
            navigate(item.navigateToURL)
        }
    }

    const handleSelectorClick = index => {
        setCurrentPage(index)
        resetInterval()
    }

    return (
        <div
            className='rds-carrousel-hero rds-relative'
            style={{ margin: margin ? `${margin}px` : undefined }}
        >
            <StyledCarrouselHeroInner
                className='rds-carrousel-hero_inner'
                transitionSpeed={transitionSpeed}
                currentPage={currentPage}
                height={height}
            >
                {items.map((item, index) => (
                    <StyledSliderItem
                        className='rds-relative'
                        onClick={() => handleItemClick(item)}
                        item={item}
                        key={index}
                    >
                        <StyledSliderItemPicture
                            className='rds-relative'
                            item={item}
                            themeContext={themeContext}
                        />
                        <StyledSliderItemHeader
                            className='rds-carrousel-hero_header rds-absolute'
                            item={item}
                            padding={padding}
                        >
                            <Header {...item.header} />
                        </StyledSliderItemHeader>
                    </StyledSliderItem>
                ))}
            </StyledCarrouselHeroInner>
            <div className='rds-carrousel-hero_selectors rds-absolute rds-flex rds-align-center'>
                {items.map((item, index) => (
                    <StyledCarrouselHeroSelector
                        key={index}
                        isActive={currentPage === index}
                        transitionSpeed={transitionSpeed}
                        onClick={() => handleSelectorClick(index)}
                    />
                ))}
            </div>
        </div>
    )
}

export default Slider
